import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation, useParams } from 'react-router-dom';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Button,
  Tooltip,
  Divider
} from '@mui/material';
import { 
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Folder as FolderIcon,
  Settings as SettingsIcon,
  AccountCircle as AccountCircleIcon,
  Logout as LogoutIcon,
  Groups as GroupsIcon,
  Feedback as FeedbackIcon,
  Security as SecurityIcon,
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Add as AddIcon,
  Description,
  DescriptionOutlined,
  Help as HelpIcon,
  AutoFixHigh as AutoFixHighIcon
} from '@mui/icons-material';

import useAuth from '../../hooks/useAuth';
import PermissionGate from '../common/PermissionGate';
import ProfileImageUpload from './ProfileImageUpload';
import useNotification from '../../hooks/useNotification';
import codeAwareConversationService from '../../services/codeAwareConversationService';
import CacheStatusIndicator from '../common/CacheStatusIndicator';
import { CircularProgress } from '@mui/material';

const ConversationLayout = ({ children, project, conversation, onSaveDocument, onNewConversation }) => {
  const { user, logout } = useAuth();
  const { showNotification } = useNotification();
  const navigate = useNavigate();
  const location = useLocation();
  const { projectId } = useParams(); // Fallback to get projectId from URL
  
  const [anchorEl, setAnchorEl] = useState(null);
  const [navMenuEl, setNavMenuEl] = useState(null);
  const [returnPath, setReturnPath] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isWorkspaceValid, setIsWorkspaceValid] = useState(false);
  
  // Always return to the foundation page when back button is clicked
  useEffect(() => {
    const id = project?._id || projectId;
    if (id) {
      // Always set the return path to the foundation page
      setReturnPath(`/projects/${id}/prd-workflow#foundation`);
    }
  }, [project, projectId]);

  // Validate workspace when project changes
  useEffect(() => {
    const validateWorkspace = async () => {
      const id = project?._id || projectId;
      if (id) {
        try {
          const result = await codeAwareConversationService.validateProjectWorkspace(id);
          setIsWorkspaceValid(result.data.isValid);
        } catch (error) {
          console.error('Failed to validate workspace:', error);
          setIsWorkspaceValid(false);
        }
      }
    };

    validateWorkspace();
  }, [project, projectId]);

  // Get current task ID from URL
  const getCurrentTaskId = () => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    return urlSearchParams.get('task');
  };

  // Handle generate task conversation
  const handleGenerateTask = async () => {
    const taskId = getCurrentTaskId();
    const id = project?._id || projectId;
    
    if (!id || !taskId || !isWorkspaceValid) {
      showNotification({
        message: 'Cannot generate: missing project, task, or invalid workspace',
        type: 'error'
      });
      return;
    }

    try {
      setIsGenerating(true);

      await codeAwareConversationService.generateTaskConversationWithCallbacks(
        id,
        taskId,
        {
          onMessage: (message) => {
            console.log('Generation message:', message);
            // Could show real-time updates in a toast or progress indicator
            if (message.type === 'assistant' && message.message) {
              // Could add streaming content to conversation in real-time
            }
          },
          onError: (error) => {
            console.error('Generation error:', error);
            showNotification({
              message: `Generation failed: ${error.message}`,
              type: 'error'
            });
          },
          onComplete: (messages) => {
            console.log('Generation complete for task:', taskId);
            showNotification({
              message: `AI content generated successfully for ${taskId}`,
              type: 'success'
            });
            
            // Refresh the page to show the new conversation content
            window.location.reload();
          }
        }
      );

    } catch (error) {
      console.error('Error generating task:', error);
      showNotification({
        message: `Failed to generate task content: ${error.message}`,
        type: 'error'
      });
    } finally {
      setIsGenerating(false);
    }
  };
  
  const [profileImageError, setProfileImageError] = useState(false);

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleNavMenuOpen = (event) => {
    setNavMenuEl(event.currentTarget);
  };
  
  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };
  
  const handleNavMenuClose = () => {
    setNavMenuEl(null);
  };
  
  const handleLogout = async () => {
    try {
      await logout();
      showNotification({
        message: 'Logged out successfully',
        type: 'success'
      });
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      showNotification({
        message: 'Logout failed',
        type: 'error'
      });
    }
    handleProfileMenuClose();
  };
  
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <AppBar 
        position="fixed" 
        color="inherit" 
        elevation={0} 
        sx={{ 
          zIndex: (theme) => theme.zIndex.drawer + 1,
          borderBottom: '1px solid rgba(0,0,0,0.1)',
          bgcolor: '#485869', 
          color: 'white'
        }}
      >
        <Toolbar variant="regular" sx={{ height: 72 }}>
          <IconButton
            edge="start"
            aria-label="back"
            onClick={() => navigate(returnPath || `/projects/${project?._id || projectId}`)}
            sx={{ mr: 2, color: 'white' }}
          >
            <ArrowBackIcon />
          </IconButton>
          
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="subtitle1" component="div" sx={{ mr: 1, fontWeight: 'medium', color: 'white' }}>
              {/* Display only the current task name from URL params */}
              {(() => {
                const urlSearchParams = new URLSearchParams(window.location.search);
                const taskId = urlSearchParams.get('task');
                
                const taskTitles = {
                  'chat-prd': 'Project Info',
                  'project-info': 'Project Info',
                  'extract-prd': 'PRD Extraction',
                  'field-validations': 'Field Validation',
                  'tech-stack': 'Technology Stack',
                  'dev-guidelines': 'Development Guidelines',
                  'design-guidelines': 'Design Guidelines',
                  'wireframes': 'Wireframes',
                  'designs': 'Designs',
                  'design-system': 'Design System',
                  'security-guidelines': 'Security Guidelines',
                  'api-guidelines': 'API Guidelines',
                  'features': 'Features',
                  'user-stories': 'User Stories',
                  'architecture': 'Architecture',
                  'process-flows': 'Process Flows',
                  'user-roles': 'User Roles'
                };
                
                return taskId ? (taskTitles[taskId] || taskId) : (project?.name || 'Project');
              })()}
            </Typography>
          </Box>
          
          <Divider orientation="vertical" flexItem sx={{ mx: 1, bgcolor: 'rgba(255,255,255,0.1)' }} />
          
          <Box sx={{ flexGrow: 1 }} />
          
          {/* Cache Status Indicator */}
          {conversation?.id && (
            <Box sx={{ mr: 1 }}>
              <CacheStatusIndicator 
                conversationId={conversation.id} 
                variant="chip"
              />
            </Box>
          )}
          
          {/* Generate with AI button */}
          {(project?._id || projectId) && isWorkspaceValid && getCurrentTaskId() && (
            <Tooltip title={isGenerating ? 'Generating...' : 'Generate with AI'}>
              <IconButton
                color="inherit"
                onClick={handleGenerateTask}
                disabled={isGenerating}
                sx={{ 
                  mr: 1,
                  border: '2px solid rgba(255,255,255,0.3)',
                  color: 'white',
                  width: 36,
                  height: 36,
                  '&:hover': {
                    borderColor: 'white',
                    bgcolor: 'rgba(255,255,255,0.1)'
                  },
                  '&:disabled': {
                    color: 'rgba(255,255,255,0.5)',
                    borderColor: 'rgba(255,255,255,0.2)'
                  }
                }}
              >
                {isGenerating ? (
                  <CircularProgress size={16} sx={{ color: 'rgba(255,255,255,0.7)' }} />
                ) : (
                  <AutoFixHighIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          )}
          
          {/* Save Document button */}
          {onSaveDocument && (
            <Tooltip title="Save as Document">
              <IconButton
                color="inherit"
                onClick={onSaveDocument}
                sx={{ 
                  mr: 1,
                  border: '2px solid rgba(255,255,255,0.3)',
                  color: 'white',
                  width: 36,
                  height: 36,
                  '&:hover': {
                    borderColor: 'white',
                    bgcolor: 'rgba(255,255,255,0.1)'
                  }
                }}
              >
                <SaveIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          
          {/* New Conversation button with just + icon */}
          {onNewConversation && (
            <PermissionGate permission="conversation.create">
              <Tooltip title="New Conversation">
                <IconButton
                  color="inherit"
                  onClick={onNewConversation}
                  sx={{
                    mr: 2,
                    border: '2px solid rgba(255,255,255,0.3)',
                    color: 'white',
                    width: 36,
                    height: 36,
                    '&:hover': {
                      borderColor: 'white',
                      bgcolor: 'rgba(255,255,255,0.1)'
                    }
                  }}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </PermissionGate>
          )}
          
          <IconButton
            size="small"
            edge="end"
            aria-label="account"
            aria-haspopup="true"
            onClick={handleProfileMenuOpen}
            sx={{ 
              p: 0.5,
              border: '2px solid',
              borderColor: 'rgba(255,255,255,0.2)'
            }}
          >
            {user?.profileImage && !profileImageError ? (
              <Box
                component="img"
                key={user.profileImage}
                src={user.profileImage}
                alt={user?.name || 'User'}
                onError={() => setProfileImageError(true)}
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  objectFit: 'cover'
                }}
              />
            ) : (
              <Avatar sx={{
                width: 32,
                height: 32,
                bgcolor: 'rgba(255,255,255,0.2)',
                color: 'white',
                fontSize: '0.875rem',
                fontWeight: 600
              }}>
                {user?.name?.charAt(0) || 'U'}
              </Avatar>
            )}
          </IconButton>
        </Toolbar>
      </AppBar>
      
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          elevation: 3,
          sx: { 
            mt: 1,
            width: 250,
            overflow: 'visible',
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          }
        }}
      >
        <ProfileImageUpload size={80} onClose={handleProfileMenuClose} />
        <Box sx={{ px: 2, py: 1, textAlign: 'center' }}>
          <Typography variant="subtitle2" fontWeight={600}>
            {user?.name || 'User'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {user?.email || 'user@example.com'}
          </Typography>
        </Box>
        <Divider />
        <MenuItem onClick={() => {
          handleProfileMenuClose();
          navigate('/settings');
        }}
        sx={{ 
          borderRadius: 1,
          mx: 1,
          my: 0.5
        }}
        >
          <ListItemIcon>
            <AccountCircleIcon fontSize="small" sx={{ color: 'primary.main' }} />
          </ListItemIcon>
          <ListItemText>Profile</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => {
          handleProfileMenuClose();
          navigate('/settings');
        }}
        sx={{ 
          borderRadius: 1,
          mx: 1,
          my: 0.5
        }}
        >
          <ListItemIcon>
            <SettingsIcon fontSize="small" sx={{ color: 'grey.700' }} />
          </ListItemIcon>
          <ListItemText>Settings</ListItemText>
        </MenuItem>

        {/* Archived Projects */}
        <PermissionGate permission="admin.archived.projects">
          <MenuItem onClick={() => {
            handleProfileMenuClose();
            navigate('/projects/archived');
          }}
          sx={{ borderRadius: 1, mx: 1, my: 0.5 }}
          >
            <ListItemIcon>
              <FolderIcon fontSize="small" sx={{ color: 'grey.700' }} />
            </ListItemIcon>
            <ListItemText>Archived projects</ListItemText>
          </MenuItem>
        </PermissionGate>

        {/* User Management */}
        <PermissionGate permission="admin.user.management">
          <MenuItem onClick={() => {
            handleProfileMenuClose();
            navigate('/users');
          }}
          sx={{ borderRadius: 1, mx: 1, my: 0.5 }}
          >
            <ListItemIcon>
              <GroupsIcon fontSize="small" sx={{ color: 'primary.main' }} />
            </ListItemIcon>
            <ListItemText>User management</ListItemText>
          </MenuItem>
        </PermissionGate>

        {/* Feedback Management */}
        <PermissionGate permission="admin.feedback">
          <MenuItem onClick={() => {
            handleProfileMenuClose();
            navigate('/admin/feedback');
          }}
          sx={{ borderRadius: 1, mx: 1, my: 0.5 }}
          >
            <ListItemIcon>
              <FeedbackIcon fontSize="small" sx={{ color: 'info.main' }} />
            </ListItemIcon>
            <ListItemText>Feedback management</ListItemText>
          </MenuItem>
        </PermissionGate>

        {/* User Permission */}
        <PermissionGate permission="admin.user.permission">
          <MenuItem onClick={() => {
            handleProfileMenuClose();
            navigate('/user-permission');
          }}
          sx={{ borderRadius: 1, mx: 1, my: 0.5 }}
          >
            <ListItemIcon>
              <SecurityIcon fontSize="small" sx={{ color: '#666' }} />
            </ListItemIcon>
            <ListItemText>User permission</ListItemText>
          </MenuItem>
        </PermissionGate>

        <Divider />
        <MenuItem onClick={handleLogout}
        sx={{ 
          borderRadius: 1,
          mx: 1,
          my: 0.5,
          color: 'error.main'
        }}
        >
          <ListItemIcon>
            <LogoutIcon fontSize="small" sx={{ color: 'error.main' }} />
          </ListItemIcon>
          <ListItemText>Logout</ListItemText>
        </MenuItem>
      </Menu>
      
      <Box 
        sx={{ 
          flexGrow: 1, 
          overflow: 'hidden',
          mt: '80px', // Adjusted to match MainLayout padding
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center', // Center child elements horizontally
          justifyContent: 'center',
          height: 'calc(100vh - 80px)',
          width: '100%',
          backgroundColor: 'transparent', // Removed background color for a cleaner look
          px: { xs: 0.5, sm: 1.5, md: 2, lg: 3, xl: 4 }, // More minimal padding for cleaner look
        }}
      >
        <Box 
          sx={{ 
            maxWidth: { xs: '95%', sm: '85%', md: '700px', lg: '750px', xl: '800px' }, // Narrower, more focused max width
            width: '100%',      // Use 100% of available width up to maxWidth
            mx: 'auto',         // Auto margins for horizontal centering
            height: '100%',     // Take full height of parent
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default ConversationLayout;